import { useEffect, useRef, useState } from "react";
import { Campo, type PropsDoCampo } from "../ui";
import { ErroDaApi } from "../../nucleo/api";
import {
  cepCompleto,
  consultarCep,
  mascararCep,
  type EnderecoDoCep,
} from "../../nucleo/cep";

const MENSAGEM_PADRAO =
  "Não foi possível consultar o CEP agora. Preencha o endereço à mão.";

interface Props extends Omit<
  PropsDoCampo,
  "value" | "onChange" | "rotulo" | "ajuda" | "erro"
> {
  rotulo?: string;
  /** O CEP, com ou sem máscara. */
  value: string;
  /** Recebe o CEP já com a máscara. */
  onChange: (cep: string) => void;
  /**
   * Chamado quando a consulta acha o CEP. Entrega **só** logradouro, bairro, cidade, UF e IBGE:
   * número e complemento não vêm do CEP e quem os digitou não os perde. Quem monta o formulário
   * decide o que fazer com cada um — nada é gravado por aqui.
   */
  onEndereco: (endereco: EnderecoDoCep) => void;
  /** Chamado quando uma consulta começa — marco para o formulário saber o que foi editado depois. */
  aoIniciarBusca?: () => void;
  /** Erro de validação do formulário (ex.: CEP incompleto no envio). */
  erro?: string;
}

/**
 * Campo de CEP com máscara 00000-000 que, ao completar 8 dígitos, consulta a API do portal e
 * entrega o endereço. A falha da consulta (CEP inexistente, provedores fora) aparece como aviso
 * no campo e **não bloqueia** nada: os demais campos seguem editáveis para preencher à mão.
 */
export function CampoDeCep({
  rotulo = "CEP",
  value,
  onChange,
  onEndereco,
  aoIniciarBusca,
  erro,
  ...resto
}: Props) {
  const [buscando, setBuscando] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);
  const controle = useRef<AbortController | null>(null);
  const aoAchar = useRef(onEndereco);
  useEffect(() => {
    aoAchar.current = onEndereco;
  });
  // Ao desmontar, a consulta em voo é descartada: nada de preencher formulário que já fechou.
  useEffect(() => () => controle.current?.abort(), []);

  /* A consulta nasce da DIGITAÇÃO, não do valor: abrir a edição de um endereço já gravado, com
     CEP completo, não pode sair buscando e sobrescrevendo o que a equipe conferiu. */
  function aoDigitar(bruto: string) {
    const mascarado = mascararCep(bruto);
    onChange(mascarado);
    controle.current?.abort();
    setFalha(null);
    if (!cepCompleto(mascarado)) {
      setBuscando(false);
      return;
    }
    const atual = new AbortController();
    controle.current = atual;
    setBuscando(true);
    aoIniciarBusca?.();
    consultarCep(mascarado, atual.signal)
      .then((endereco) => {
        if (atual.signal.aborted) return;
        setBuscando(false);
        aoAchar.current(endereco);
      })
      .catch((erroDaConsulta: unknown) => {
        if (atual.signal.aborted) return;
        setBuscando(false);
        setFalha(
          // Só a mensagem escrita pela API (tem `codigo`) é dita à pessoa; "falha de rede" e
          // "A API respondeu 500" não ajudam quem só quer saber se pode digitar à mão.
          erroDaConsulta instanceof ErroDaApi && erroDaConsulta.codigo !== null
            ? erroDaConsulta.message
            : MENSAGEM_PADRAO,
        );
      });
  }

  return (
    <Campo
      {...resto}
      rotulo={rotulo}
      value={value}
      onChange={(evento) => aoDigitar(evento.target.value)}
      inputMode="numeric"
      autoComplete="postal-code"
      placeholder="00000-000"
      maxLength={9}
      erro={erro ?? falha ?? undefined}
      ajuda={buscando ? <span role="status">Buscando…</span> : undefined}
      aria-busy={buscando || undefined}
    />
  );
}
